import React from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { LogIn, UserPlus, LayoutDashboard, LogOut, User as UserIcon, ShieldCheck } from 'lucide-react';

/**
 * Lightweight layout for public (unauthenticated) surfaces:
 * homepage, events list, event detail, RSVP stub, login/register.
 *
 * Keeps design tokens intact - navy primary header, Playfair display wordmark.
 */
const PublicLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || (user?.email?.[0] || '').toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-neutral-100">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-192.png" alt="Dostination" className="w-9 h-9 object-contain drop-shadow-sm" />
            <span className="font-display text-xl font-bold text-primary-900">Dostination</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-neutral-700">
            <Link to="/" className="hover:text-primary-900 transition-colors">Home</Link>
            <Link to="/events" className="hover:text-primary-900 transition-colors">Events</Link>
            <a href="#about" className="hover:text-primary-900 transition-colors">About</a>
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Button
                  onClick={() => navigate(['admin', 'super_admin'].includes(user.role) ? '/admin' : '/my-events')}
                  className="bg-primary-800 hover:bg-primary-900 text-white shadow-soft"
                  size="sm"
                  data-testid="public-nav-dashboard-btn"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  {['admin', 'super_admin'].includes(user.role) ? 'Admin' : 'My Dostination'}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="public-nav-user-menu-trigger"
                      className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 text-white font-display font-bold flex items-center justify-center shadow-soft hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      aria-label="Open account menu"
                    >
                      {initials || '?'}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuLabel className="truncate text-xs text-neutral-500">
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="public-nav-menu-profile">
                      <UserIcon className="h-4 w-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings/security')} data-testid="public-nav-menu-security">
                      <ShieldCheck className="h-4 w-4 mr-2" /> Security
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="public-nav-menu-logout" className="text-accent-700 focus:text-accent-800">
                      <LogOut className="h-4 w-4 mr-2" /> Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/auth/login')} className="text-primary-800 hover:text-primary-900">
                  <LogIn className="h-4 w-4 mr-2" />
                  Sign in
                </Button>
                <Button size="sm" onClick={() => navigate('/auth/register')} className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-primary-900 font-semibold shadow-gold hover:from-secondary-600 hover:to-secondary-700">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Join
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Page */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer id="about" className="mt-16 border-t border-neutral-100 bg-neutral-50">
        <div className="container mx-auto px-4 md:px-8 py-12 grid gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/logo-192.png" alt="Dostination" className="w-8 h-8 object-contain" />
              <span className="font-display text-lg font-bold text-primary-900">Dostination</span>
            </div>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Premium, warm, cultured, intentional gatherings and travel experiences
              for a curated South-Asian &amp; adjacent community.
            </p>
          </div>
          <div>
            <h4 className="font-display text-sm font-semibold text-primary-900 mb-3 uppercase tracking-wider">Explore</h4>
            <ul className="space-y-2 text-sm text-neutral-600">
              <li><Link to="/events" className="hover:text-primary-900">Events</Link></li>
              <li><Link to="/" className="hover:text-primary-900">Home</Link></li>
              <li><Link to="/pricing" className="hover:text-primary-900" data-testid="footer-pricing-link">Pricing</Link></li>
              <li><Link to="/auth/register" className="hover:text-primary-900">Join Dostination</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm font-semibold text-primary-900 mb-3 uppercase tracking-wider">Values</h4>
            <ul className="space-y-2 text-sm text-neutral-600">
              <li>Premium &amp; understated</li>
              <li>Warm &amp; hospitable</li>
              <li>Cultured &amp; intentional</li>
              <li>Social, never transactional</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-neutral-200 py-4 text-center text-xs text-neutral-500">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-2">
            {/* Bundle 75 Task 4 - Stripe-required Terms + community-safety links.
                Bundle 78 Task 2 - Law enforcement protocols link. */}
            <a href="/terms" className="hover:text-primary-900" data-testid="footer-terms-link">Terms</a>
            <span aria-hidden="true">*</span>
            <a href="/acceptable-use" className="hover:text-primary-900" data-testid="footer-aup-link">Acceptable Use</a>
            <span aria-hidden="true">*</span>
            <a href="/privacy" className="hover:text-primary-900" data-testid="footer-privacy-link">Privacy</a>
            <span aria-hidden="true">*</span>
            <a href="/pricing" className="hover:text-primary-900" data-testid="footer-pricing-legal-link">Pricing</a>
            <span aria-hidden="true">*</span>
            <a href="/law-enforcement" className="hover:text-primary-900" data-testid="footer-law-enforcement-link">Law Enforcement</a>
            <span aria-hidden="true">*</span>
            <a href="/terms#anti-trafficking" className="hover:text-secondary-800 text-secondary-700" data-testid="footer-community-safety-link">Community Safety</a>
            <span aria-hidden="true">*</span>
            <a href="mailto:yaar@dostination.com" className="hover:text-primary-900" data-testid="footer-contact-link">Contact</a>
          </div>
          © {new Date().getFullYear()} Dostination - Curated with care.
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
